/**
 * ブラウザ通知（Web Push）の購読と後始末。
 *
 * - 宛先の登録は varuna の `register_push_token/`（Firebase ID トークン必須）。
 *   uuid だけの旧 `update_device_token/` は送信に使われないので呼ばない
 * - 対象は PC ブラウザだけ（スマホはアプリへ誘導、アプリ WebView はネイティブの FCM と二重になる）
 * - ON/OFF は「このブラウザで受け取るか」なので localStorage に持つ
 * - ログアウト（別アカウントへの切り替えを含む）で宛先を消す。
 *   消さないと共用 PC で前の利用者宛ての通知（会社名入り）が届き続ける
 * - 通知から来たページ（?push=<log_id>）で開封を記録する。どのページに着地しても記録できるよう App で見る
 *
 * 状態はモジュール級で1つ（カードと App が同じものを見る）。`install()` は App の setup から1回だけ呼ぶ。
 */
import { computed, ref, watch } from 'vue'
import { StorageSerializers, useLocalStorage } from '@vueuse/core'
import { useRoute } from 'vue-router/composables'
import type { MessagePayload, Messaging } from 'firebase/messaging'
import { firebaseConfig, getFirebaseApp } from '@/infra/firebase/app'
import { useAuth, type AuthUser } from '@/composables/useAuth'
import { useSite } from '@/composables/useSite'
import {
  deletePushToken,
  recordPushOpened,
  registerPushToken
} from '@/services/push-client'
import {
  foregroundNotificationKey,
  isMobileUserAgent,
  isWebPushConfigured
} from '@/utils/push/webPushTarget'

const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY || ''
const APP_ID = process.env.VUE_APP_FIREBASE_APPID || ''
const SW_PATH = '/firebase-messaging-sw.js'
// FCM の既定スコープ。ページを制御しないので PWA キャッシュ用の Service Worker と干渉しない
const SW_SCOPE = '/firebase-cloud-messaging-push-scope'
const FG_CLAIM_KEY = 'web_push_fg_claim'
const FG_CLAIM_TTL_MS = 10000

type Permission = NotificationPermission | 'unsupported'

const enabled = useLocalStorage('web_push_enabled', false)
const permission = ref<Permission>(
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
)
const supported = ref(false)
let messagingPromise: Promise<Messaging | null> | null = null
/**
 * このブラウザで登録した宛先。ログアウト直後に画面遷移（/login）が走ると解除リクエストが
 * 途中で切れるため、localStorage に残しておき、次に開いたページでも解除をやり直せるようにする。
 */
const registration = useLocalStorage<{ uid: string; token: string } | null>(
  'web_push_registration',
  null,
  { serializer: StorageSerializers.object }
)
let opChain: Promise<unknown> = Promise.resolve()
let foregroundListening = false
const openedIds = new Set<string>()

const messagingModule = () => import('firebase/messaging')

/** 登録と解除を直列にする。OFF 直後の ON などで、遅れて届いた削除が新しい宛先を消さないため */
function serialized<T>(task: () => Promise<T>): Promise<T> {
  const next = opChain.then(task, task)
  opChain = next.catch(() => undefined)
  return next
}

/** firebase/messaging は対象環境でだけ読み込む（スマホに余計なチャンクを配らない） */
function loadMessaging(): Promise<Messaging | null> {
  if (!messagingPromise) {
    messagingPromise = messagingModule()
      .then(async (m) =>
        (await m.isSupported()) ? m.getMessaging(getFirebaseApp()) : null
      )
      .catch((e) => {
        console.warn('[WebPush] 初期化に失敗しました', e)
        return null
      })
  }
  return messagingPromise
}

/**
 * FCM 用 Service Worker を登録する。
 * public/ の SW は process.env を読めないので、Firebase の設定をクエリで渡す。
 * getRegistration はスコープを含む別の SW（PWA のルート SW）を返すことがあるので、スクリプトを確かめる。
 */
async function swRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_SCOPE)
  const worker =
    existing && (existing.active || existing.waiting || existing.installing)
  if (existing && worker && worker.scriptURL.includes(SW_PATH)) return existing
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    projectId: firebaseConfig.projectId,
    messagingSenderId: firebaseConfig.messagingSenderId || '',
    appId: APP_ID
  })
  return navigator.serviceWorker.register(`${SW_PATH}?${params.toString()}`, {
    scope: SW_SCOPE
  })
}

/** 有効化前の Worker を getToken に渡すと購読が InvalidStateError で落ちるので、activated まで待つ */
function whenActivated(
  reg: ServiceWorkerRegistration
): Promise<ServiceWorkerRegistration> {
  const worker = reg.installing || reg.waiting
  if (reg.active || !worker) return Promise.resolve(reg)
  return new Promise((resolve) => {
    worker.addEventListener('statechange', () => {
      // redundant（失敗）でも待ち続けない。その場合は getToken 側で失敗する
      if (worker.state === 'activated' || worker.state === 'redundant') {
        resolve(reg)
      }
    })
  })
}

function register(user: AuthUser): Promise<boolean> {
  return serialized(async () => {
    if (!enabled.value || permission.value !== 'granted') return false
    const messaging = await loadMessaging()
    if (!messaging) return false
    try {
      const m = await messagingModule()
      const token = await m.getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: await whenActivated(await swRegistration())
      })
      if (!token) return false
      // 登録リクエストの**前に**記録する。通信中にログアウトして画面遷移すると、このページでは
      // 解除も記録もできないまま終わるため、次のページの install() が解除できるようにしておく
      registration.value = { uid: user.uid, token }
      await registerPushToken(await user.getIdToken(), token)
      // 保存中に OFF・ログアウト・別アカウントへの切り替えがあったら、今の登録を取り消す
      const current = useAuth().user.value
      if (!enabled.value || !current || current.uid !== user.uid) {
        await deletePushToken(user.uid, token)
          .then(() => {
            if (registration.value?.token === token) registration.value = null
          })
          .catch(() => undefined)
        return false
      }
      return true
    } catch (e) {
      console.warn('[WebPush] 宛先の登録に失敗しました', e)
      return false
    }
  })
}

/**
 * 端末の購読とサーバの宛先を消す。片方が失敗してももう片方は必ず実行する。
 *
 * ログアウト直後は画面遷移でリクエストが切れうるので、ブラウザ内で完結する購読解除を先に済ませ、
 * サーバの宛先は**消せたと確認できてから**記録を消す（切れたら次のページで install() がやり直す）。
 */
function unregister(): Promise<void> {
  return serialized(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE)
      const sub = reg ? await reg.pushManager.getSubscription() : null
      if (sub) await sub.unsubscribe()
    } catch (e) {
      console.warn('[WebPush] 端末側の購読解除に失敗しました', e)
    }
    const saved = registration.value
    if (saved) {
      try {
        await deletePushToken(saved.uid, saved.token)
        registration.value = null
      } catch (e: unknown) {
        // 4xx（会員が退会済みなど）は再試行しても変わらないので記録を消す。通信エラー・5xx は残してやり直す
        const status = (e as { response?: { status?: number } })?.response
          ?.status
        if (status && status < 500) registration.value = null
        console.warn('[WebPush] サーバ側の宛先解除に失敗しました', e)
      }
    }
    // 登録したことがなく Messaging も読み込んでいない環境（スマホ・Web Push 無効の環境）では
    // 破棄するトークンが無い。ログアウトのたびに firebase/messaging のチャンクを読み込まない
    if (!saved && !messagingPromise) return
    try {
      const messaging = await loadMessaging()
      if (messaging) await (await messagingModule()).deleteToken(messaging)
    } catch (e) {
      // 購読は上で解除済み。FCM 側のトークン破棄だけなので失敗しても実害はない
    }
  })
}

/** 同じ通知を複数タブで出さない（localStorage はタブ間で共有される） */
function claimForegroundUnlocked(key: string): boolean {
  try {
    const now = Date.now()
    const prev = JSON.parse(window.localStorage.getItem(FG_CLAIM_KEY) || 'null')
    if (prev && prev.key === key && now - prev.at < FG_CLAIM_TTL_MS)
      return false
    window.localStorage.setItem(FG_CLAIM_KEY, JSON.stringify({ key, at: now }))
  } catch (e) {
    // 使えない環境では出す（無通知よりよい）
  }
  return true
}

/**
 * FCM は開いている全タブに同じメッセージを渡すので、読んで書くまでを Web Locks でタブ間の排他にする
 * （排他しないと、両方のタブが書き込み前の値を読んで両方とも表示する）
 */
async function claimForeground(key: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return claimForegroundUnlocked(key)
  }
  try {
    return await navigator.locks.request(FG_CLAIM_KEY, () =>
      claimForegroundUnlocked(key)
    )
  } catch (e) {
    return claimForegroundUnlocked(key)
  }
}

/**
 * タブを開いている間は OS 通知が自動では出ないので自分で出す。
 * クリック時の遷移は SW の notificationclick が data.link を読む。
 */
async function showForeground(payload: MessagePayload) {
  const title = payload.notification?.title || 'ヤギッシュ'
  const body = payload.notification?.body || ''
  const data = payload.data
  const key = foregroundNotificationKey(data, title, body)
  if (!(await claimForeground(key))) return
  const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE)
  await reg?.showNotification(title, {
    body,
    icon: '/favicon.ico',
    // 通知ごとに別のタグにする。同じタグだと置き換わり、届いていないように見える。
    // notification_id が無いときは同じ文面どうしだけ置き換わるよう、重複判定と同じ key を使う
    tag: data?.notification_id
      ? `yagish-${data.type || 'notification'}-${data.notification_id}`
      : `yagish-${key}`,
    data: { link: payload.fcmOptions?.link || '' }
  })
}

async function listenForeground() {
  if (foregroundListening) return
  const messaging = await loadMessaging()
  if (!messaging || foregroundListening) return
  foregroundListening = true
  ;(await messagingModule()).onMessage(messaging, (payload) => {
    showForeground(payload).catch((e) =>
      console.warn('[WebPush] 通知の表示に失敗しました', e)
    )
  })
}

export function useWebPush() {
  const { user, whenAuthReady } = useAuth()
  const { isAppWebView } = useSite()

  const isTarget = computed(
    () =>
      !isAppWebView.value &&
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      permission.value !== 'unsupported' &&
      !isMobileUserAgent(navigator.userAgent) &&
      isWebPushConfigured(APP_ID, VAPID_KEY)
  )
  const available = computed(() => isTarget.value && supported.value)

  /** ON にする。ユーザー操作の中で呼ぶ（許可ダイアログはユーザー操作が必要） */
  async function enable(): Promise<'enabled' | 'denied' | 'failed'> {
    const result = await Notification.requestPermission()
    permission.value = result
    if (result !== 'granted') {
      enabled.value = false
      return result === 'denied' ? 'denied' : 'failed'
    }
    enabled.value = true
    if (user.value && (await register(user.value))) return 'enabled'
    // 宛先が登録できていないのに「オン」と見せない
    enabled.value = false
    return 'failed'
  }

  async function disable() {
    enabled.value = false
    await unregister()
  }

  /** App の setup から1回だけ呼ぶ */
  function install() {
    const route = useRoute()

    if (isTarget.value) {
      loadMessaging().then((m) => {
        supported.value = !!m
      })
    }

    // 別タブで ON にされた場合（localStorage 経由で共有）も、このタブで受信できるようにする。
    // 表示中のタブがあると FCM は OS 通知を出さずページへ渡すので、リスナーが無いと通知が消える
    watch(
      enabled,
      (on) => {
        if (on && isTarget.value) listenForeground()
      },
      { immediate: true }
    )

    // ログイン状態が確定してから始める（復元前の null をログアウトと取り違えないため）
    whenAuthReady().then(() => {
      // 前のページでログアウトして解除が途中で切れていたら、ここでやり直す
      const saved = registration.value
      if (saved && (!user.value || user.value.uid !== saved.uid)) unregister()

      watch(
        user,
        (next, prev) => {
          if (prev && (!next || next.uid !== prev.uid)) unregister()
          if (next && isTarget.value && enabled.value) register(next)
        },
        { immediate: true }
      )
    })

    watch(
      [() => route.query.push, user],
      async ([push, u]) => {
        const id = String((Array.isArray(push) ? push[0] : push) || '')
        if (!u || !/^\d+$/.test(id) || openedIds.has(id)) return
        openedIds.add(id)
        try {
          await recordPushOpened(await u.getIdToken(), id)
        } catch (e) {
          console.warn('[WebPush] 開封の記録に失敗しました', e)
        }
      },
      { immediate: true }
    )
  }

  return { available, enabled, permission, enable, disable, install }
}
